<template>
	<BaseLayout :footer-full="true" :include-padding="false" background-color-class="bg-color-greige">
		<header class="advisor-hero marketing-section bg-color-white">
			<div class="container">
				<div class="align-items-center display-flex-md gutters-300-md">
					<div class="col-6-md">
						<h1 class="heading-xl">Give your clients a better alternative</h1>
						<p class="delta line-height-150"
							>Enable your clients to invest in institutional-quality alternative investments like real
							estate and venture capital.</p
						>

						<div class="hero-ctas display-flex flex-wrap-wrap mt-300 gap-150 mb-300-smo">
							<BaseLink :link="createAccountLink" classes="button button-primary flex-1-lg" />
							<BaseLink :link="investmentsContact" classes="button button-outline flex-1-lg" />
							<p
								class="display-flex align-items-center gap-50 module-cta module-radius-lg p-100 mt-0 width-100"
							>
								<BaseSvgIcon name="envelope-closed" width="18" height="18" class="mr-25" />
								Are your clients qualified purchasers?
								<BaseLink :link="investmentsContact" classes="text-link">Contact us</BaseLink>
							</p>
						</div>
					</div>
					<div class="col-6-md">
						<figure class="my-0 mx-0 mx-300-lg">
							<picture>
								<source
									:srcset="
										appCloudfrontPath + '/images/marketing-site/advisor/advisor-landing-hero.webp'
									"
									type="image/webp"
								/>
								<img
									:src="appCloudfrontPath + '/images/marketing-site/advisor/advisor-landing-hero.png'"
									alt="graphic of client dashboard modules"
								/>
							</picture>
						</figure>
					</div>
				</div>
			</div>
		</header>
		<PartnerLogosMarquee :is-flat="true" />
		<section class="marketing-section container bg-color-greige advisor-section--info-cards">
			<InfoCards :cards="investmentsCards" :swap-order="true" card-heading-style="heading-1" />
		</section>
		<section class="marketing-section bg-color-content-primary bg-img-dotted-waves">
			<div class="container">
				<div
					class="display-flex align-items-center flex-wrap-wrap gap-300 justify-content-space-between text-color-white"
				>
					<div class="col-12 col-7-md">
						<p class="overline text-color-gray-40 mb-100">How it works</p>
						<h3 class="heading-1">Get your clients invested in 3 simple steps</h3>
					</div>
					<BaseLink :link="createAccountLink" classes="button button-outline-reversed" />
				</div>

				<div class="display-flex flex-wrap-wrap-smo gap-300 mt-300">
					<article
						v-for="(step, s) in howItWorksSteps"
						:key="`step-${s}`"
						class="width-100 bg-color-white p-200"
					>
						<BaseSvgIcon :name="step.icon" width="80" height="56" class="display-block mb-100" />
						<h4 class="heading-5">{{ step.heading }}</h4>
						<p class="delta">{{ step.summary }}</p>
					</article>
				</div>
			</div>
		</section>
		<section class="premium-investor-relations marketing-section bg-color-greige">
			<div class="container">
				<div class="display-flex-md gutters-200 align-items-center">
					<div class="col-6-md mb-200-smo">
						<picture>
							<source :srcset="investorRelationsData.image.webp" type="image/webp" />
							<img
								class="image"
								:src="investorRelationsData.image.url"
								:alt="investorRelationsData.image.alt"
							/>
						</picture>
					</div>
					<div class="col-6-md">
						<h2 class="heading-1"> {{ investorRelationsData.heading }}</h2>
						<div class="delta mt-150" v-html="investorRelationsData.description"> </div>
						<BaseLink :link="investmentsContact" classes="mt-200 button button-outline" />
					</div>
				</div>
			</div>
		</section>
		<section class="marketing-section bg-color-content-primary text-color-white">
			<div class="container">
				<div class="display-flex-md flex-wrap-wrap justify-content-center">
					<div class="col-12 col-9-md col-8-lg">
						<h2 class="heading-1">How we're regulated</h2>
						<h3 class="heading-6 mt-200">'40 Act Funds</h3>
						<p class="mt-25">
							The
							<a
								href="https://www.sec.gov/cgi-bin/browse-edgar?CIK=1777677"
								target="_blank"
								rel="noopener noreferrer"
								class="text-color-current-color text-decoration-underline text-link--dark-mode"
								>Flagship Real Estate Fund</a
							>,
							<a
								href="https://www.sec.gov/cgi-bin/browse-edgar?company=Fundrise+Income+real+estate+Fund"
								target="_blank"
								rel="noopener noreferrer"
								class="text-color-current-color text-decoration-underline text-link--dark-mode"
								>Income Real Estate Fund</a
							>, and
							<a
								href="https://www.sec.gov/edgar/browse/?CIK=1867090"
								target="_blank"
								rel="noopener noreferrer"
								class="text-color-current-color text-decoration-underline text-link--dark-mode"
								>Innovation Fund</a
							>
							are registered investment companies under the Investment Company Act of 1940, each of whom's
							securities offerings have been registered with the US Securities and Exchange Commission
							("SEC"). Each fund's prospectus and other information can be found on the SEC's EDGAR
							website using the links above. Registration with the SEC does not imply approval of the
							merits of any offering.
						</p>
						<h3 class="heading-6 mt-200">Private Funds</h3>
						<p class="mt-25">
							The Fundrise eREIT and eFund securities offerings have been qualified with the SEC under
							Regulation A of the Securities Act of 1933. Annual audited financials, as well as regular
							material updates, for our eREITs and eFund are filed with the SEC. Copies of the publicly
							filed offering circulars may be found at
							<BaseLink
								:link="circularsLink"
								classes="text-color-current-color text-decoration-underline text-link--dark-mode"
							/>. Qualification with the SEC does not imply approval of the merits of any offering.
						</p>
						<h3 class="heading-6 mt-200">The Manager</h3>
						<p class="mt-50">
							Fundrise Advisors, LLC is a registered investment advisor with the SEC and is regulated
							under the rules of the Investment Advisors Act of 1940. While such registration does not
							imply a certain level of skill, it does require us to follow federal regulations that aim to
							protect investors. By law, we must provide investment advice that is in the best interest of
							our clients.
						</p>
						<p class="mt-25">
							Our parent company, Rise Companies Corp., and its family of sponsored investment funds have
							reporting standards similar to those of publicly traded companies. You can find
							<a
								href="https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=0001640967&owner=include&count=40&hidefilings=0?"
								target="_blank"
								rel="noopener noreferrer"
								class="text-color-current-color text-decoration-underline text-link--dark-mode"
								>annual audited financials</a
							>, as well as other material information, for Rise Companies Corp. on the SEC EDGAR website.
						</p>
					</div>
				</div>
			</div>
		</section>
		<section class="marketing-section bg-color-white">
			<div class="container">
				<div class="display-flex-md flex-wrap-wrap justify-content-center">
					<div class="col-12 col-9-md col-8-lg">
						<h3 class="heading-2 font-weight-lighter text-align-center mb-150">Important details</h3>
						<QuestionList :question-items="importantDetails" />
					</div>
				</div>
			</div>
		</section>
	</BaseLayout>
</template>

<script setup lang="ts">
import { InfoCard, Link, QuestionItem } from 'types/layout';
import { appCloudfrontPath } from '@constants';
import InfoCards from '@components/layout/info-cards.vue';
import PartnerLogosMarquee from '@components/marketing-site/partner-logos-marquee.vue';
import QuestionList from '@components/layout/question-list.vue';
import { useHead } from '@unhead/vue';

const createAccountLink: Link = {
	text: 'Create account',
	router: 'advisor-signup'
};
const investmentsContact: Link = {
	text: 'Connect with us',
	router: 'contact',
	query: {
		isAdvisor: true
	}
};

const circularsLink: Link = {
	text: 'fundrise.com/oc',
	router: 'offering-circulars',
	target: '_blank'
};

const investorRelationsData = {
	description:
		"Advisors and their clients receive direct access to our in-house Investor Relations team made up of alumni from Vanguard, Citi Private Bank, and more. We're prepared to answer any questions you may have, on the phone or via email, and will work to make your Fundrise investing experience as smooth as possible.",
	heading: 'First-class support',
	image: {
		webp: `${appCloudfrontPath}/images/marketing-site/advisor/contact-investor-relations-team.webp`,
		url: `${appCloudfrontPath}/images/marketing-site/advisor/contact-investor-relations-team.png`,
		alt: 'Fundrise Investor Relations team'
	}
};

const howItWorksSteps = [
	{
		icon: 'form',
		heading: 'Create a free Advisor Account',
		summary: 'You can create your account to get started in less than 2 minutes for free.'
	},
	{
		icon: 'invite',
		heading: 'Invite your clients to invest',
		summary:
			'Send a personalized invitation from Fundrise to your clients. Your clients must sign an authorization form to give you permission to be their advisor.'
	},
	{
		icon: 'data-track',
		heading: 'Track and report on their progress',
		summary: 'You will see all of your clients’ data - including their performance, assets across funds, and more.'
	}
];

const investmentsCards: Array<InfoCard> = [
	{
		heading: 'Real Estate and Venture Capital',
		title: 'Give your clients access to the private markets',
		description: [
			'Bolster the traditional portfolio of public securities with direct access to private real estate and venture capital.'
		],
		imageUrl: '/images/marketing-site/advisor/info-card-offerings.webp',
		fallbackImageUrl: '/images/marketing-site/advisor/info-card-offerings.png',
		link: {
			text: 'See our investment opportunities',
			router: 'offerings'
		}
	},
	{
		heading: 'User Experience',
		title: 'We handle everything',
		description: [
			"When your clients are on our platform, we handle everything from transaction processing to performance reporting, so you can focus on your clients' needs."
		],
		imageUrl: '/images/marketing-site/advisor/info-card-user-experience.webp',
		fallbackImageUrl: '/images/marketing-site/advisor/info-card-user-experience.png'
	},
	{
		heading: 'Bank-level Security',
		title: 'We protect your clients’ information and capital',
		description: [
			'To safeguard your data, all communication with Fundrise is protected with end-to-end Transport Layer Security (TLS) and your sensitive data is encrypted at-rest using the Advanced Encryption Standard (AES), the same method utilized by banks.'
		],
		imageUrl: '/images/marketing-site/advisor/info-card-security.webp',
		fallbackImageUrl: '/images/marketing-site/advisor/info-card-security.png'
	}
];

const importantDetails: Array<QuestionItem> = [
	{
		question: 'Is there any cost to me?',
		answer: 'No. There is no cost to an advisor at any stage. Create an account, invite as many clients as you want, view and report on their progress, all with no cost to you.'
	},
	{
		question: 'What are the fees for my clients?',
		answer: '<p>Fundrise charges its individual clients an annual advisory fee of 0.15%.  In addition, each investment fund pays us an annual asset management fee as described in each investment fund’s offering documents. To learn more about Fundrise’s fees, <a href="https://fundrise.com/help/articles/360032879331" target="_blank" class="text-color-current-color text-decoration-underline">click here.</a></p> <p>Any fees you charge your clients must be done through a separate agreement.</p>'
	},
	{
		question: 'How do I officially connect to a client’s account as their advisor?',
		answer: 'You must first create an advisor account with Fundrise. Once your advisor account is created, you can invite new or current Fundrise users via email to connect with your advisor account. Once they are prompted to connect to your advisor account, they will have to download, fill out, and email us an authorization form. Once received, we will approve the relationship and you will have access to view details about their account.'
	},
	{
		question: 'Can I transact on behalf of my clients?',
		answer: 'No. At this time, Fundrise only supports non-discretionary relationships between advisors and clients.'
	},
	{
		question: 'Will I receive a referral fee for inviting clients?',
		answer: 'No. There is no referral fee or compensation of any kind for advisors inviting clients.'
	}
];

async function setup(): Promise<void> {
	useHead({
		title: 'Become an advisor',
		meta: [
			{ property: 'og:title', content: 'Advisors' },
			{
				name: 'description',
				content:
					'Enable your clients to invest in institutional-quality alternative investments like real estate and venture capital'
			},
			{
				property: 'og:description',
				content:
					'Enable your clients to invest in institutional-quality alternative investments like real estate and venture capital'
			}
		]
	});
}
setup();
</script>

<style lang="scss" scoped>
@use '../../styles/constants/colors' as *;
@use '../../styles/utilities/respond-to.scss' as *;

.advisor-hero {
	.hero-ctas {
		flex-direction: column;
		@include respond-to(lg) {
			flex-direction: row;
		}
	}
	.module-cta {
		border: solid 1px gray(20);

		a {
			white-space: nowrap;
		}
	}
}

.premium-investor-relations {
	.image {
		max-width: 375px;
		@include respond-to(smo) {
			margin: 0 auto;
		}
	}
}
</style>
